import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Header } from "@/components/Header";
import { Button } from "@/components/Button";
import { CoverPage } from "@/components/CoverPage";
import { SettingsTab } from "@/components/tabs/SettingsTab";
import { HomeTab } from "@/components/tabs/HomeTab";
import { useRosterState } from "@/hooks/useRosterState";
import { dutyUsage } from "@/utils/dutyOptions";
import { useWebSocket } from "@/hooks/useWebSocket";
import { useUserActivity } from "@/hooks/useUserActivity";
import type { ActiveViewer } from "@/components/ActiveViewers";
import { useSession } from "@/contexts/SessionContext";
import { useNotification } from "@/contexts/NotificationContext";
import { setSaveQueueFailureHandler } from "@/utils/saveQueue";
import { getClientId, setApiRetryNotifier, setViewOnlyBlockedNotifier, VIEW_ONLY_MESSAGE } from "@/api/client";
import { EDIT_TRACE_TTL_MS, gridTraces, includedSeqFor, stripEchoMeta } from "@/utils/stateSync";
import type { GridChange, StateDelta } from "@/hooks/useRosterState";
import { usernameColor } from "@/utils/userColor";
import type { OperationMessage } from "@/types/api";
import { registerViewState, reloadedView } from '@/utils/reloadState';

type Tab = "counter" | "officers" | "settings";

/** How long to wait for an edit's own WebSocket echo before refetching instead. */
const ECHO_WAIT_MS = 3000;
/** The echo is sent just before the edit's HTTP response, so it may arrive a
 *  moment before the caller asks to reconcile; one that recent still counts. */
const ECHO_ARRIVAL_SLACK_MS = 1000;

const tabIcons: Record<Tab, string> = {
  counter: "📋",
  officers: "👤",
  settings: "⚙️",
};

export default function App() {
  const { sessionId, userName, clearSession } = useSession();

  // Show cover page until a session is established
  if (!sessionId) {
    return <CoverPage />;
  }

  return <RosterApp sessionId={sessionId} userName={userName} onSwitchSession={clearSession} />;
}


function RosterApp({
  sessionId,
  userName,
  onSwitchSession,
}: {
  sessionId: string;
  userName: string | null;
  onSwitchSession: () => void;
}) {
  // After an app-update reload, come back on the tab the user was on.
  const [activeTab, setActiveTab] = useState<Tab>(() => reloadedView<Tab>('tab') ?? "counter");
  // Anchors the Inbox button portals into, so it renders inline with the tab bar
  // (desktop tab row / mobile bottom nav) instead of taking its own row inside HomeTab.
  const [desktopInboxAnchor, setDesktopInboxAnchor] = useState<HTMLDivElement | null>(null);
  const [mobileInboxAnchor, setMobileInboxAnchor] = useState<HTMLDivElement | null>(null);
  // Same portal pattern for the Notes and Contacts buttons that sit alongside Inbox.
  const [desktopNotesAnchor, setDesktopNotesAnchor] = useState<HTMLDivElement | null>(null);
  const [mobileNotesAnchor, setMobileNotesAnchor] = useState<HTMLDivElement | null>(null);
  const [desktopContactsAnchor, setDesktopContactsAnchor] = useState<HTMLDivElement | null>(null);
  const [mobileContactsAnchor, setMobileContactsAnchor] = useState<HTMLDivElement | null>(null);
  const [desktopNoReliefAnchor, setDesktopNoReliefAnchor] = useState<HTMLDivElement | null>(null);
  const [mobileNoReliefAnchor, setMobileNoReliefAnchor] = useState<HTMLDivElement | null>(null);
  // Bumped on every "no_relief_update" WS message so the No Relief call log re-fetches.
  const [noReliefUpdateTick, setNoReliefUpdateTick] = useState(0);
  // Bumped on every "call_ack_update" WS message so the officer phone reminders re-fetch.
  const [callAckUpdateTick, setCallAckUpdateTick] = useState(0);
  const { canEdit } = useSession();
  const canEditRef = useRef(canEdit);
  canEditRef.current = canEdit;
  // Mobile bottom nav has room for only the 3 main tabs — Inbox/Notes/Contacts
  // collapse behind a "More" button that pops a small menu up from the bottom right.
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  // Bumped on every "notes_update" WS message, so an open Notes panel re-fetches
  // the same way the roster canvas re-renders off a snapshot_update — otherwise
  // a lock/unlock by another user only shows up the next time someone reopens it.
  const [notesUpdateTick, setNotesUpdateTick] = useState(0);
  // Anchor the Split/Stack view control portals into — sits left of Inbox in the
  // desktop tab row (desktop-only control, so no mobile anchor is needed for it).
  const [splitControlAnchor, setSplitControlAnchor] = useState<HTMLDivElement | null>(null);
  // Badge on Home tab when changes happened while user was on a non-home tab
  const [homeTabBadge, setHomeTabBadge] = useState(false);
  // Incremented when an external user (not us) changes the roster — clears HomeTab's redo state
  const [externalChangeTick, setExternalChangeTick] = useState(0);
  // Inbox: accumulated error messages from add operations
  const [operationMessages, setOperationMessages] = useState<OperationMessage[]>([]);
  // Edit traces: highlighted cells from another user's recent change
  // Tagged with the session they were computed for: cell coordinates mean nothing in
  // any other roster, and a regenerate moves us to a new session without remounting.
  const [editTraceState, setEditTraceState] = useState<{
    sessionId: string;
    traces: Array<{ row: number; col: number; color: string; traceType: 'add' | 'delete' | 'change' }>;
  }>({ sessionId, traces: [] });
  const editTraces = useMemo(
    () => (editTraceState.sessionId === sessionId ? editTraceState.traces : []),
    [editTraceState, sessionId],
  );
  // Traces are a "just changed" cue, so they expire (EDIT_TRACE_TTL_MS after the latest edit)
  // instead of waiting to be redrawn by whatever next repaints the grid, long after the edit.
  const editTraceTimer = useRef<number | null>(null);
  useEffect(() => () => { if (editTraceTimer.current !== null) window.clearTimeout(editTraceTimer.current); }, []);
  // Live presence: cells other users currently have open for editing, keyed by user name
  const [cellPresence, setCellPresence] = useState<Record<string, { row: number; col: number; officerKey?: string }>>({});
  // Other users with this session open and in use (tab visible, not idle) — the header avatars
  const [viewers, setViewers] = useState<ActiveViewer[]>([]);

  // isDev: shows debug matrices (qx / dev usernames)
  // isAdmin: shows admin config panel in Settings (admin / qx / dev usernames)
  const lowerName = (userName ?? '').toLowerCase();
  const isDevUser = ['qx', 'dev'].includes(lowerName);
  const isAdmin = ['admin', 'qx', 'dev'].includes(lowerName);

  // Refs for values used in WS callback — prevents WS reconnect on tab toggle
  const activeTabRef = useRef(activeTab);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);
  useEffect(() => registerViewState('tab', () => activeTabRef.current), []);

  // Clear roster tab badge when user navigates to counter or officers
  useEffect(() => {
    if (activeTab === 'counter' || activeTab === 'officers') {
      setHomeTabBadge(false);
    }
  }, [activeTab]);

  // Hooks
  const { notifyInfo, notifyWarning, notifyError } = useNotification();

  // Register spinner toast for user-triggered requests that hit a 502 mid-deploy.
  // Auto-dismissed when the operation's success/error notification arrives.
  useEffect(() => {
    setApiRetryNotifier(() => {
      notifyInfo('Just a moment — we’re updating the app. Your request will go through automatically.', true, 15000);
    });
    return () => setApiRetryNotifier(null);
  }, [notifyInfo]);

  // A queued edit that could not be written even after every retry. Reported
  // here rather than in the editor, because by then the editor is usually
  // closed and the user is somewhere else entirely — but the roster on their
  // screen is now showing a change the server never took.
  useEffect(() => {
    setSaveQueueFailureHandler((_key, err) => {
      const detail = err instanceof Error ? err.message : String(err);
      notifyError(`An edit could not be saved — check your connection and try again. (${detail})`);
    });
    return () => setSaveQueueFailureHandler(null);
  }, [notifyError]);

  const { state, loading, error, transientError, refetch, applySnapshot, applyRemoteSnapshot, applyStateDelta, applyOwnEcho, patchFromOperation, patchHistoryPointer, optimisticRemoveOfficer, optimisticClearOfficerCounters, optimisticClearOfficerRanges, optimisticMoveBlock } = useRosterState();
  // Who is on each non-counter deployment, for Settings' remove confirmation.
  const dutyUse = useMemo(
    () => dutyUsage(state?.current_snapshot?.duty_slots, state?.officer_metadata),
    [state?.current_snapshot?.duty_slots, state?.officer_metadata],
  );
  // A write refused because this tab is view-only (see api/client). Say why,
  // and re-read the roster: some edits draw optimistically before their request
  // goes out, and that change never reached the server. Throttled so a burst of
  // refused writes (one gesture can fire several) costs one refetch.
  const lastViewOnlyBlockRef = useRef(0);
  useEffect(() => {
    setViewOnlyBlockedNotifier(() => {
      const now = Date.now();
      if (now - lastViewOnlyBlockRef.current < 1500) return;
      lastViewOnlyBlockRef.current = now;
      notifyWarning(VIEW_ONLY_MESSAGE);
      refetch();
    });
    return () => setViewOnlyBlockedNotifier(null);
  }, [notifyWarning, refetch]);

  const [dismissedTransientError, setDismissedTransientError] = useState<string | null>(null);

  // When this tab last received its own edit back over the WebSocket.
  const lastOwnEchoAtRef = useRef(0);

  // Ref so WS handler can read current state without being in its dep array
  const stateRef = useRef(state);
  stateRef.current = state;

  // Reset dismissed state when a different transient error appears
  useEffect(() => {
    if (transientError && transientError !== dismissedTransientError) {
      setDismissedTransientError(null);
    }
  }, [transientError, dismissedTransientError]);

  // Auto-redirect to CoverPage only on initial load failure (session not found / expired).
  // Now safe: error is only set when hasState.current === false (no state ever loaded),
  // so background refetch failures no longer trigger accidental session exits.
  useEffect(() => {
    if (error && (error.includes('not found') || error.toLowerCase().includes('session'))) {
      onSwitchSession();
    }
  }, [error, onSwitchSession]);

  // Return to counter tab if roster is cleared while on officers tab
  useEffect(() => {
    if (!state?.generated && activeTab === 'officers') {
      setActiveTab('counter');
    }
  }, [state?.generated, activeTab]);

  // Warn before accidental page refresh when a roster is active
  useEffect(() => {
    if (!state?.generated) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [state?.generated]);

  // Inbox: callback for AddOfficersTab to push operation error messages
  const handleOperationMessage = useCallback((msg: OperationMessage) => {
    setOperationMessages((prev) => [...prev, msg]);
  }, []);

  // WebSocket for real-time updates (session-scoped)
  // Use wss:// on HTTPS pages to avoid Mixed Content blocking in production
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  // client_id lets the server leave this tab out of a broadcast whose state the
  // tab's own request already returned (undo/redo/restore).
  const wsUrl = `${wsProtocol}//${window.location.host}/api/ws/roster?session_id=${sessionId}&client_id=${encodeURIComponent(getClientId())}${userName ? `&user_name=${encodeURIComponent(userName)}` : ''}`;

  const handleWsMessage = useCallback(
    (message: { type: string; data?: Record<string, unknown> }) => {
      if (message.type === 'cell_focus') {
        const d = message.data as { user_name?: string; row?: number; col?: number; officer_key?: string } | undefined;
        if (d?.user_name && d.user_name !== userName && typeof d.row === 'number' && typeof d.col === 'number') {
          setCellPresence(prev => ({ ...prev, [d.user_name!]: { row: d.row!, col: d.col!, officerKey: d.officer_key } }));
        }
        return;
      }
      if (message.type === 'cell_blur') {
        const d = message.data as { user_name?: string } | undefined;
        if (d?.user_name) {
          setCellPresence(prev => {
            if (!(d.user_name! in prev)) return prev;
            const next = { ...prev };
            delete next[d.user_name!];
            return next;
          });
        }
        return;
      }
      if (message.type === 'users_update') {
        const d = message.data as { viewers?: Array<{ user_name?: string; editing?: boolean }> } | undefined;
        if (!Array.isArray(d?.viewers)) return;
        setViewers(d.viewers
          .filter(v => typeof v.user_name === 'string' && v.user_name !== userName)
          .map(v => ({ userName: v.user_name!, editing: !!v.editing })));
        return;
      }
      if (message.type === 'presence_update') {
        const d = message.data as { cells?: Array<{ user_name: string; row: number; col: number; officer_key?: string }> } | undefined;
        const next: Record<string, { row: number; col: number; officerKey?: string }> = {};
        for (const c of d?.cells ?? []) {
          if (c.user_name !== userName) next[c.user_name] = { row: c.row, col: c.col, officerKey: c.officer_key };
        }
        setCellPresence(next);
        return;
      }
      if (message.type === 'session_forked') {
        // Someone regenerated. This roster is untouched and stays editable — the
        // notice is an invitation, not an eviction, so we deliberately do NOT
        // switch anyone over. Whoever wants the new roster keys in the ID.
        const d = message.data as
          { new_session_id?: string; changed_by?: string; at?: string } | undefined;
        if (!d?.new_session_id || d.changed_by === userName) return;

        // Filed in the Inbox as well as toasted. The toast is gone as soon as it
        // is dismissed, and this ID is the only route to the roster that was just
        // generated — nobody should have to keep their own list of session IDs.
        handleOperationMessage({
          type: 'new_session',
          timestamp: new Date(d.at ?? Date.now()).toLocaleTimeString([], {
            hour: '2-digit', minute: '2-digit',
          }),
          newSessionId: d.new_session_id,
          createdBy: d.changed_by,
        });

        const who = d.changed_by ? `${d.changed_by} generated` : 'Someone generated';
        notifyWarning(
          `${who} a new roster in session ${d.new_session_id}. This roster is unchanged — ` +
          `join the new session by ID if you want the new one. The ID is in your Inbox.`,
        );
        return;
      }
      if (message.type === 'no_relief_update') {
        const d = message.data as { changed_by?: string } | undefined;
        if (d?.changed_by === userName) return; // our own tick already updated local state
        setNoReliefUpdateTick(t => t + 1);
        return;
      }
      if (message.type === 'call_ack_update') {
        const d = message.data as { changed_by?: string } | undefined;
        if (d?.changed_by === userName) return; // our own write already updated local state
        setCallAckUpdateTick(t => t + 1);
        return;
      }
      if (message.type === 'notes_update') {
        const d = message.data as { changed_by?: string } | undefined;
        if (d?.changed_by === userName) return; // our own write already updated local state
        setNotesUpdateTick(t => t + 1);
        return;
      }
      if ((message.type !== 'snapshot_update' && message.type !== 'state_delta') || !message.data) return;

      const meta = message.data as { _changed_by?: string; _client_id?: string; _action?: string };
      const changedBy = meta._changed_by ?? null;
      // This tab's own edit, echoed back. No traces or toast — the user just made this
      // change. Only the originating TAB counts as its own: the same user's edits from
      // another tab or device are shown like anyone else's.
      const own = meta._client_id === getClientId();
      const mySeq = includedSeqFor(message.data, getClientId());
      if (own) lastOwnEchoAtRef.current = Date.now();

      // An edit arrives as a patch of what changed (utils/statePatch); a full state
      // only when the tab joins the session.
      let change: GridChange | null;
      if (message.type === 'state_delta') {
        change = applyStateDelta(message.data as unknown as StateDelta, mySeq, own);
      } else {
        const next = stripEchoMeta(message.data);
        change = { before: stateRef.current?.current_snapshot?.counter_matrix, after: next.current_snapshot?.counter_matrix };
        if (own) applyOwnEcho(next, mySeq); else applyRemoteSnapshot(next, mySeq);
      }
      if (own || !changedBy) return;

      const traces = change ? gridTraces(change.before, change.after, usernameColor(changedBy)) : [];
      if (traces.length > 0) {
        // New traces merge in by (row, col) instead of replacing the set outright, so one
        // edit doesn't erase the still-showing highlight from an earlier one; all of them
        // expire together EDIT_TRACE_TTL_MS after the latest.
        setEditTraceState(prev => {
          const kept = prev.sessionId === sessionId ? prev.traces : [];
          const merged = new Map(kept.map(t => [`${t.row}:${t.col}`, t]));
          for (const t of traces) merged.set(`${t.row}:${t.col}`, t);
          return { sessionId, traces: Array.from(merged.values()) };
        });
        if (editTraceTimer.current !== null) window.clearTimeout(editTraceTimer.current);
        editTraceTimer.current = window.setTimeout(() => {
          editTraceTimer.current = null;
          setEditTraceState(prev => (prev.traces.length ? { sessionId: prev.sessionId, traces: [] } : prev));
        }, EDIT_TRACE_TTL_MS);
      }

      // Brief toast notification (auto-dismisses after 3s)
      const action = meta._action ?? null;
      let notifText = `${changedBy} updated the roster`;
      if (action === 'undo') notifText = `${changedBy} undo`;
      else if (action === 'redo') notifText = `${changedBy} redo`;
      else if (action === 'restore') notifText = `${changedBy} restored a version`;
      notifyInfo(notifText, false);
      setExternalChangeTick(t => t + 1);

      const onRosterTab = activeTabRef.current === 'counter' || activeTabRef.current === 'officers';
      if (!onRosterTab) setHomeTabBadge(true);
    },
    [userName, sessionId, applyRemoteSnapshot, applyStateDelta, applyOwnEcho, notifyInfo, notifyWarning, handleOperationMessage],
  );

  const { connected, everConnected, sendMessage } = useWebSocket(wsUrl, handleWsMessage);
  const connectedRef = useRef(connected);
  connectedRef.current = connected;

  // After an edit whose response already patched the grid, the rest of the state
  // (history, metadata, counts) arrives with this tab's own WebSocket echo, which
  // the server sends just before that response. Fall back to a refetch only when
  // the socket is down, or the echo has not turned up shortly after.
  const reconcileAfterEdit = useCallback(() => {
    const editedAt = Date.now();
    if (!connectedRef.current) {
      void refetch();
      return;
    }
    window.setTimeout(() => {
      if (lastOwnEchoAtRef.current < editedAt - ECHO_ARRIVAL_SLACK_MS) void refetch();
    }, ECHO_WAIT_MS);
  }, [refetch]);

  // isReconnecting: true while we're waiting to reconnect after a drop
  const isReconnecting = everConnected && !connected;

  // Broadcasts that this user opened/closed a cell for editing (live presence)
  // A view-only tab is only looking — it doesn't show up as editing a cell.
  const handleCellFocus = useCallback((row: number, col: number, officerKey?: string) => {
    if (!canEditRef.current) return;
    sendMessage({ type: 'cell_focus', data: { row, col, officer_key: officerKey } });
  }, [sendMessage]);
  const handleCellBlur = useCallback(() => {
    sendMessage({ type: 'cell_blur' });
  }, [sendMessage]);

  // Tell the room whether this tab is in use, so others' avatars of us come and go:
  // re-sent on every (re)connect, since a new connection starts out counted as active.
  const userActive = useUserActivity();
  useEffect(() => {
    if (!connected) return;
    sendMessage({ type: 'presence_state', data: { active: userActive, editing: canEdit } });
  }, [connected, userActive, canEdit, sendMessage]);
  // Nobody is known to be here while we're cut off from the room.
  useEffect(() => { if (!connected) setViewers([]); }, [connected]);

  const cellPresenceList = useMemo(
    () => Object.entries(cellPresence).map(([userName, cell]) => ({ userName, ...cell })),
    [cellPresence],
  );

  // If the stored session no longer exists on the backend (404), clear it and
  // go back to the cover page so the user can create or join a fresh session.
  useEffect(() => {
    if (error?.includes('[404]')) {
      onSwitchSession();
    }
  }, [error, onSwitchSession]);

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <h2>Loading roster...</h2>
      </div>
    );
  }

  // error is only set on initial load failure (no state ever loaded) — safe to show full-page
  if (error && !error.includes('[404]')) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#dc3545" }}>
        <h2>Error loading roster</h2>
        <p>{error}</p>
        <Button onClick={refetch}>Retry</Button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100dvh", background: "var(--mode-bg, #f8f9fa)" }}>
      <Header
        sessionId={sessionId}
        currentUser={userName ?? undefined}
        isReconnecting={isReconnecting}
        onSwitchSession={onSwitchSession}
        viewers={viewers}
      />

      {/* Transient error banner: background refetch failed but state is preserved.
          Hidden while reconnecting — the error is a symptom of the same outage. */}
      {transientError && transientError !== dismissedTransientError && !isReconnecting && (
        <div
          className="ui-zoom"
          style={{
            padding: "8px 20px",
            background: "#fff3cd",
            color: "#856404",
            fontSize: "12px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>
            ⚠️ Could not refresh roster data — showing last known state.
            <button
              onClick={refetch}
              style={{
                marginLeft: "10px",
                fontSize: "11px",
                padding: "2px 8px",
                borderRadius: "4px",
                border: "1px solid #856404",
                background: "transparent",
                color: "#856404",
                cursor: "pointer",
              }}
            >
              Retry
            </button>
          </span>
          <button
            onClick={() => setDismissedTransientError(transientError)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#856404", fontSize: "14px", lineHeight: 1 }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Main content — CSS adds paddingBottom on mobile via .main-content / .has-subnav */}
      <div
        className="main-content"
        style={{ maxWidth: "none", margin: "0 auto", paddingTop: "4px", paddingLeft: "10px", paddingRight: "10px" }}
      >
        {/* Desktop tab bar — hidden on mobile via .desktop-tabs CSS class.
            Flat labels on a shared baseline (the CSS border-bottom on .desktop-tabs)
            with the active tab's own bold colored segment of that line directly
            beneath it — a real tab-strip look, not a filled pill that reads as a
            pressed button. Shorter vertical padding keeps the row compact. */}
        <div className="desktop-tabs ui-zoom">
          {(["counter", "officers", "settings"] as Tab[]).map((tab) => {
            const isDisabled = tab === 'officers' && !state?.generated;
            const isActive = activeTab === tab;
            const label = tab === 'counter' ? '📋 Counter' : tab === 'officers' ? '👤 Officers' : '⚙️ Settings';
            return (
              <button
                key={tab}
                onClick={() => !isDisabled && setActiveTab(tab)}
                title={isDisabled ? 'Generate a roster first' : undefined}
                style={{
                  padding: "8px 16px",
                  // Overlaps the container's own border-bottom (2px) so the active
                  // tab's 3px colored bar reads as part of that same baseline, not
                  // a separate element floating below it.
                  marginBottom: "-2px",
                  background: "transparent",
                  border: "none",
                  borderBottom: isActive ? "3px solid #667eea" : "3px solid transparent",
                  color: isActive ? "#667eea" : isDisabled ? "#b3bbc9" : "#475569",
                  fontWeight: isActive ? 700 : 500,
                  fontSize: "13px",
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  position: "relative",
                  opacity: isDisabled ? 0.6 : 1,
                }}
              >
                {label}
                {tab === 'counter' && homeTabBadge && (
                  <span style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: '#dc3545',
                    border: '1.5px solid white',
                  }} />
                )}
              </button>
            );
          })}
          {/* Split/Stack view control portals in here once a roster exists — see HomeTab */}
          <div ref={setSplitControlAnchor} style={{ display: "contents" }} />
          {/* Inbox button portals in here once a roster exists — see HomeTab */}
          <div ref={setDesktopInboxAnchor} style={{ display: "contents" }} />
          {/* Notes/Contacts buttons portal in here once a roster exists — see HomeTab */}
          <div ref={setDesktopNotesAnchor} style={{ display: "contents" }} />
          <div ref={setDesktopContactsAnchor} style={{ display: "contents" }} />
          {/* No Relief button portals in here once a roster exists — see HomeTab */}
          <div ref={setDesktopNoReliefAnchor} style={{ display: "contents" }} />
        </div>

        {/* Counter + Officers tabs share one mounted HomeTab instance so state
            (selectedHistoryIndex, etc.) persists when switching between the two. */}
        <div style={{ display: (activeTab === 'counter' || activeTab === 'officers') ? 'block' : 'none' }}>
          <HomeTab
            activeView={activeTab === 'officers' ? 'officers' : 'counter'}
            state={state}
            refetch={refetch}
            reconcileAfterEdit={reconcileAfterEdit}
            applySnapshot={applySnapshot}
            patchFromOperation={patchFromOperation}
            patchHistoryPointer={patchHistoryPointer}
            optimisticRemoveOfficer={optimisticRemoveOfficer}
            optimisticClearOfficerCounters={optimisticClearOfficerCounters}
            optimisticClearOfficerRanges={optimisticClearOfficerRanges}
            optimisticMoveBlock={optimisticMoveBlock}
            sessionId={sessionId}
            isDevUser={isDevUser}
            operationMessages={operationMessages}
            onClearMessages={() => setOperationMessages([])}
            onSwitchTab={(tab) => setActiveTab(tab as Tab)}
            onOperationMessage={handleOperationMessage}
            editTraces={editTraces}
            externalChangeTick={externalChangeTick}
            cellPresence={cellPresenceList}
            onCellFocus={handleCellFocus}
            onCellBlur={handleCellBlur}
            inboxAnchorDesktop={desktopInboxAnchor}
            inboxAnchorMobile={mobileInboxAnchor}
            notesAnchorDesktop={desktopNotesAnchor}
            notesAnchorMobile={mobileNotesAnchor}
            contactsAnchorDesktop={desktopContactsAnchor}
            contactsAnchorMobile={mobileContactsAnchor}
            noReliefAnchorDesktop={desktopNoReliefAnchor}
            noReliefAnchorMobile={mobileNoReliefAnchor}
            noReliefUpdateTick={noReliefUpdateTick}
            callAckUpdateTick={callAckUpdateTick}
            onMobileMenuAction={() => setShowMoreMenu(false)}
            notesUpdateTick={notesUpdateTick}
            splitControlAnchor={splitControlAnchor}
          />
        </div>

        {activeTab === "settings" && (
          <SettingsTab
            generated={state?.generated || false}
            operation_mode={state?.operation_mode}
            shift_type={state?.shift_type}
            onUpdated={refetch}
            isAdmin={isAdmin}
            dutyUse={dutyUse}
          />
        )}
      </div>

      {/* Main bottom nav: always mounted; CSS shows on mobile, hides on desktop */}
      <nav className="bottom-nav">
        {(["counter", "officers", "settings"] as Tab[]).map((tab) => {
          const isDisabled = tab === 'officers' && !state?.generated;
          const label = tab === 'counter' ? 'Counter' : tab === 'officers' ? 'Officers' : 'Settings';
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              onClick={() => !isDisabled && setActiveTab(tab)}
              className="bottom-nav__btn"
              title={isDisabled ? 'Generate a roster first' : undefined}
              style={{ color: isActive ? '#667eea' : isDisabled ? '#ccc' : '#888', opacity: isDisabled ? 0.5 : 1, cursor: isDisabled ? 'not-allowed' : 'pointer' }}
            >
              <span className="bottom-nav__icon">{tabIcons[tab]}</span>
              <span className="bottom-nav__label" style={{ fontWeight: isActive ? 600 : 400 }}>
                {label}
              </span>
              {isActive && <span className="bottom-nav__indicator" />}
              {tab === 'counter' && homeTabBadge && (
                <span style={{
                  position: 'absolute',
                  top: '8px',
                  right: '10px',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#dc3545',
                  border: '1.5px solid white',
                }} />
              )}
            </button>
          );
        })}
        {/* Inbox/Notes/Contacts don't get their own nav slots on mobile — three more
            icons would crowd a 4-item row past usefulness — they collapse behind
            this single "More" button instead. */}
        {state?.generated && (
          <button
            onClick={() => setShowMoreMenu(m => !m)}
            className="bottom-nav__btn"
            style={{ color: showMoreMenu ? '#667eea' : '#888' }}
          >
            <span className="bottom-nav__icon">⋯</span>
            <span className="bottom-nav__label" style={{ fontWeight: showMoreMenu ? 600 : 400 }}>More</span>
            {showMoreMenu && <span className="bottom-nav__indicator" />}
          </button>
        )}
      </nav>

      {showMoreMenu && (
        <>
          {/* Invisible backdrop — any click outside the popover closes it */}
          <div onClick={() => setShowMoreMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 549 }} />
          {/* Popover itself also closes on click: every row is an action (open a
              modal), so closing right after is always the correct outcome. */}
          <div
            onClick={() => setShowMoreMenu(false)}
            className="more-menu-popover"
            style={{
              position: 'fixed', bottom: 'calc(64px + env(safe-area-inset-bottom) + 8px)', right: 8,
              background: 'white', borderRadius: 12, border: '1px solid #e5e7eb',
              boxShadow: '0 8px 28px rgba(0,0,0,0.2)', overflow: 'hidden', zIndex: 550,
              minWidth: 190, display: 'flex', flexDirection: 'column',
            }}
          >
            {/* Inbox/Notes/Contacts buttons portal in here — see HomeTab */}
            <div ref={setMobileInboxAnchor} style={{ display: "contents" }} />
            <div ref={setMobileNotesAnchor} style={{ display: "contents" }} />
            <div ref={setMobileContactsAnchor} style={{ display: "contents" }} />
            <div ref={setMobileNoReliefAnchor} style={{ display: "contents" }} />
          </div>
        </>
      )}

    </div>
  );
}
